const formatCurrency = (amount) => `RWF ${Math.round(amount || 0).toLocaleString()}`;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const downloadBookingInvoice = (booking) => {
  if (!booking) return;

  const customerName = [booking.customer?.firstName, booking.customer?.lastName].filter(Boolean).join(' ').trim() || 'Customer';
  const lineItems = (booking.priceItems || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.description || item.type || 'Booking item')}</td>
      <td style="text-align:right;">${formatCurrency(item.amount)}</td>
    </tr>
  `).join('');

  const logoUrl = `${window.location.origin}/images/brand/go-rides-logo-dark.svg`;
  const invoiceHtml = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Invoice ${escapeHtml(booking.reference)}</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; margin: 0; background: #f5f5f5; color: #111111; }
          .invoice { max-width: 920px; margin: 32px auto; background: white; border-radius: 24px; padding: 40px; box-shadow: 0 20px 60px rgba(17,24,39,.08); }
          .top { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; margin-bottom: 32px; }
          .logo { height: 48px; width: auto; }
          .invoice-meta { text-align: right; }
          .eyebrow { font-size: 11px; letter-spacing: .18em; text-transform: uppercase; color: #6b7280; font-weight: 700; margin-bottom: 8px; }
          h1 { margin: 0 0 6px; font-size: 34px; line-height: 1; }
          .muted { color: #6b7280; font-size: 14px; line-height: 1.6; }
          .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; margin-bottom: 28px; }
          .card { border: 1px solid #e5e7eb; border-radius: 18px; padding: 18px; background: #fafafa; }
          .label { font-size: 10px; text-transform: uppercase; letter-spacing: .16em; font-weight: 700; color: #9ca3af; margin-bottom: 8px; }
          .value { font-size: 15px; font-weight: 700; color: #111111; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .16em; color: #9ca3af; padding: 14px 0; border-bottom: 1px solid #e5e7eb; }
          td { font-size: 14px; padding: 14px 0; border-bottom: 1px solid #f1f5f9; }
          .summary { margin-top: 22px; margin-left: auto; width: 320px; }
          .summary-row { display: flex; justify-content: space-between; padding: 10px 0; font-size: 14px; }
          .summary-row.total { border-top: 1px solid #e5e7eb; margin-top: 8px; padding-top: 16px; font-size: 18px; font-weight: 800; }
          .status { display: inline-block; padding: 8px 12px; border-radius: 999px; background: #FFF7ED; color: #FF6B00; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
          .footer { margin-top: 34px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
          @media print {
            body { background: white; }
            .invoice { box-shadow: none; margin: 0; max-width: none; border-radius: 0; }
          }
        </style>
      </head>
      <body>
        <div class="invoice">
          <div class="top">
            <div>
              <img src="${logoUrl}" alt="Go Rides" class="logo" />
              <p class="muted" style="margin-top:14px;">Your Journey, Your way</p>
            </div>
            <div class="invoice-meta">
              <div class="eyebrow">Customer Invoice</div>
              <h1>Invoice</h1>
              <p class="muted">Reference: <strong>${escapeHtml(booking.reference)}</strong><br/>Issued: ${escapeHtml(booking.createdAt || booking.startDate || '')}</p>
              <div class="status">${escapeHtml(booking.paymentStatusLabel || booking.paymentStatus || 'Pending')}</div>
            </div>
          </div>

          <div class="grid">
            <div class="card">
              <div class="label">Billed To</div>
              <div class="value">${escapeHtml(customerName)}</div>
              <div class="muted">${escapeHtml(booking.customer?.email || '')}<br/>${escapeHtml(booking.customer?.phone || '')}</div>
            </div>
            <div class="card">
              <div class="label">Vehicle</div>
              <div class="value">${escapeHtml(booking.carDisplayName)}</div>
              <div class="muted">Pickup: ${escapeHtml(booking.pickup || '')}<br/>Trip: ${escapeHtml(booking.startDate || '')} - ${escapeHtml(booking.endDate || '')}</div>
            </div>
          </div>

          <div class="grid">
            <div class="card">
              <div class="label">Payment Details</div>
              <div class="value">${escapeHtml(booking.paymentMethodLabel || booking.paymentMethod || 'Pending Payment')}</div>
              <div class="muted">Transaction Ref: ${escapeHtml(booking.transactionRef || 'Pending')}<br/>Paid At: ${escapeHtml(booking.paidAt || 'Pending')}</div>
            </div>
            <div class="card">
              <div class="label">Reservation Details</div>
              <div class="value">${escapeHtml(String(booking.totalDays || 0))} day(s)</div>
              <div class="muted">Destination: ${escapeHtml(booking.destinationLocation || booking.pickup || '')}<br/>Booking Status: ${escapeHtml(booking.status || '')}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align:right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lineItems}
            </tbody>
          </table>

          <div class="summary">
            <div class="summary-row total">
              <span>Total</span>
              <span>${formatCurrency(booking.price)}</span>
            </div>
          </div>

          <div class="footer">
            <p class="muted">Thank you for booking with Go Rides. This invoice can be used as your reservation payment record.</p>
          </div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 250);
          };
        </script>
      </body>
    </html>
  `;

  const invoiceWindow = window.open('', '_blank', 'width=1080,height=900');
  if (!invoiceWindow) return;
  invoiceWindow.document.open();
  invoiceWindow.document.write(invoiceHtml);
  invoiceWindow.document.close();
};
